// In-app notification center data layer. Backs the Bell dropdown
// (components/shell/NotificationCenter.tsx). Talks to workers/src/api/
// notifications.ts (/api/notifications/*).
//
// The list is polled on an interval so the unread badge stays fresh without a
// websocket — the same lightweight approach useTicketMessages uses. Mark-read
// mutations optimistically update the cache, then invalidate to reconcile.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "@/api";

export type NotificationKind =
  | "mail"
  | "ticket"
  | "meeting"
  | "mention"
  | "booking"
  | "file"
  | "system";

export interface AppNotification {
  id: string;
  kind: NotificationKind | string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  read_at: number | null;
  created_at: number;
}

export interface NotificationsResponse {
  items: AppNotification[];
  unread_count: number;
}

// Local query key — kept here (not in the shared qk map) since this surface owns
// its own routes, mirroring how api/contacts.ts declares its endpoints inline.
const NOTIFICATIONS_KEY = ["notifications"] as const;

const BASE = "/api/notifications";

// Poll cadence for the feed. 30s is a good balance: fast enough that the badge
// feels live, slow enough to be cheap. Refetches pause automatically while the
// tab is backgrounded (tanstack-query default), then fire on refocus.
const POLL_MS = 30_000;

export function useNotifications() {
  return useQuery<NotificationsResponse>({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => http<NotificationsResponse>(BASE),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/** Convenience selector: unread count for the Bell badge, 0 while loading. */
export function useUnreadCount(): number {
  const { data } = useNotifications();
  return data?.unread_count ?? 0;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      http<{ ok: boolean }>(`${BASE}/${id}/read`, { method: "POST" }),
    // Optimistically flip the row + decrement the badge so the UI feels instant.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const prev = qc.getQueryData<NotificationsResponse>(NOTIFICATIONS_KEY);
      qc.setQueryData<NotificationsResponse>(NOTIFICATIONS_KEY, (cur) => {
        if (!cur) return cur;
        let dropped = 0;
        const items = cur.items.map((n) => {
          if (n.id === id && !n.read) {
            dropped = 1;
            return { ...n, read: true, read_at: Date.now() };
          }
          return n;
        });
        return { items, unread_count: Math.max(0, cur.unread_count - dropped) };
      });
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(NOTIFICATIONS_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      http<{ ok: boolean; marked: number }>(`${BASE}/read-all`, { method: "POST" }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const prev = qc.getQueryData<NotificationsResponse>(NOTIFICATIONS_KEY);
      qc.setQueryData<NotificationsResponse>(NOTIFICATIONS_KEY, (cur) =>
        cur
          ? {
              items: cur.items.map((n) => (n.read ? n : { ...n, read: true, read_at: Date.now() })),
              unread_count: 0,
            }
          : cur,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(NOTIFICATIONS_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}
